import { createMasterProductApi } from "@/api/services/admin/master-data/product";
import { TCreateMasterProductReqDto } from "@/api/services/admin/master-data/product/dtos/create-master-product.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MASTER_DATA_TYPE } from "@/features/master-data/constants/masterDataType";
import { ProductFormFields } from "@/features/master-data/components/Product/ProductFormFields";
import { masterDataProductSchema } from "@/features/master-data/schema/masterDataProductSchema";
import { TMasterDataProductForm } from "@/features/master-data/types/masterDataProductForm";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const initValue: TMasterDataProductForm = {
  name: "",
  logo: "",
  introductionPageUrl: "",
  apiEndpoint: "",
  singleCharLabel: "",
  publishedAt: null,
};

const listRoute = getDynamicRoute(AppRoutes.masterDataDetail, { type: MASTER_DATA_TYPE.PRODUCT });

const MasterDataProductCreatePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const methods = useForm<TMasterDataProductForm>({
    defaultValues: initValue,
    resolver: yupResolver(masterDataProductSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;

  const onSubmit = async (data: TMasterDataProductForm) => {
    try {
      const payload: TCreateMasterProductReqDto = {
        name: data.name,
        logo: data.logo,
        introductionPageUrl: data.introductionPageUrl || null,
        apiEndpoint: data.apiEndpoint,
        singleCharLabel: data.singleCharLabel,
        publishedAt: data.publishedAt ? data.publishedAt.toISOString() : null,
      };
      await createMasterProductApi(payload);
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MASTER_DATA.FETCH_PRODUCT_LIST] });
      navigate(listRoute);
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    }
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <ProductFormFields control={control} />
            </CardContent>
          </Card>
        ),
      },
    ],
    [control],
  );

  return (
    <Form height="100%" methods={methods} onSubmit={onSubmit} schema={masterDataProductSchema}>
      <Stack gap={3}>
        <Stack gap={1}>
          <Typography variant="head20Bold" color={text.primary}>
            プロダクトの新規追加
          </Typography>
        </Stack>

        <MenuNavigate
          contents={contents}
          contentAffix={
            <StickyFooter justifyContent="space-between">
              <Button size="sm" variant="ghost" onClick={() => navigate(listRoute)}>
                戻る
              </Button>
              <Button size="sm" type="submit">
                保存する
              </Button>
            </StickyFooter>
          }
        />
      </Stack>
    </Form>
  );
};

export default MasterDataProductCreatePage;
